import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import {
    Grid,
    Paper,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Chip,
    IconButton,
    Collapse,
    InputAdornment,
    // Aliased since the page once imported chart.js's Tooltip plugin
    // alongside it. The charts are gone, but the alias stays: it keeps every
    // call site unambiguous and stops the clash coming back.
    Tooltip as MuiTooltip,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    LinearProgress,
    CircularProgress,
    withStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "antd";
import PhoneMissedIcon from "@material-ui/icons/PhoneMissed";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import CallMissedOutgoingIcon from "@material-ui/icons/CallMissedOutgoing";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import moment from "moment";
import XLSX from "xlsx";

// Categorical slots 1-3, validated as a set against a white chart surface on
// the all-pairs check (worst CVD dE 9.2, worst normal-vision dE 24.0).
// Assigned to departments by fixed order and never cycled - Sales is always
// blue whether or not Design has rows that day, so a filter cannot repaint the
// survivors.
//
// Aqua sits at 2.82:1 on white, under the 3:1 bar, so the bar chart ships
// visible value labels and the full table below: magnitude and identity are
// never carried by the fill alone.
const DEPT_COLORS = { Sales: "#2a78d6", Design: "#eb6834", Operations: "#1baf7a" };
const DEPT_ORDER = ["Sales", "Design", "Operations"];
const FALLBACK_COLOR = "#7a7a75";

const INK = "#1c1c1a";
const INK_SOFT = "#5c5b57";
const INK_MUTED = "#8a8984";
const GRID = "#ececea";
const SURFACE = "#ffffff";

const colorFor = (dept) => DEPT_COLORS[dept] || FALLBACK_COLOR;

// Magnitude scale for the missed-call counts, shaded RELATIVE to the worst
// employee that day - an absolute threshold would paint every row red on a bad
// day and every row blue on a quiet one, telling a manager nothing about who to
// talk to first. Deliberately the same status hues as stageTone, so no new
// palette is introduced, and the number always sits beside the bar: magnitude
// is never carried by colour alone.
const countTone = (value, max) => {
    if (!value) return { bg: "#f1f4f8", fg: INK_MUTED, bar: "#c9d2de" };
    const share = max > 0 ? value / max : 0;
    if (share >= 0.66) return { bg: "#fdecea", fg: "#b03a2e", bar: "#c0483f" };
    if (share >= 0.33) return { bg: "#fff4e0", fg: "#9a6212", bar: "#d29433" };
    return { bg: "#eef2f8", fg: ORANGE_INK, bar: "#6f9fd8" };
};

// Stage colouring is a STATUS scale, kept away from the categorical hues above
// so a stage chip can never be mistaken for a department.
const stageTone = (stage = "") => {
    const s = stage.toLowerCase();
    if (!stage) return { bg: "#f0f0ee", fg: INK_MUTED };
    if (s.includes("lost") || s.includes("junk")) return { bg: "#fdecea", fg: "#b03a2e" };
    if (s.includes("completed") || s.includes("won") || s.includes("sign-off")) return { bg: "#e8f6ef", fg: "#17734f" };
    if (s.includes("hold") || s.includes("marketing")) return { bg: "#fff4e0", fg: "#9a6212" };
    return { bg: "#eef2f8", fg: ORANGE_INK };
};

// One branch of the hierarchy: a clickable header row plus its children.
// Collapsed by default at every level except the top - a department with eight
// centres and forty managers expanded on arrival is unreadable.
const TreeNode = ({ nodeKey, level, label, sub, count, max, classes, expanded, onToggle, children }) => {
    const open = !!expanded[nodeKey];
    const tone = countTone(count, max);
    const pct = count > 0 && max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 0;
    const badge = LEVEL_BADGES[level] || LEVEL_BADGES.department;

    return (
        <div>
            <div className={classes.treeRow} onClick={() => onToggle(nodeKey)}>
                {open
                    ? <KeyboardArrowDownIcon className={classes.treeCaret} />
                    : <KeyboardArrowRightIcon className={classes.treeCaret} />}
                <span className={classes.levelBadge} style={{ background: badge.bg, color: badge.fg }}>
                    {badge.label}
                </span>
                <div style={{ minWidth: 0 }}>
                    <div className={classes.treeLabel}>{label}</div>
                    {sub ? <div className={classes.treeSub}>{sub}</div> : null}
                </div>
                <span className={classes.treeCount}>
                    {/* Bars are scaled to the PARENT's total, not the global
                        maximum, so each level answers "how much of this branch
                        is this child" rather than shrinking to nothing deeper
                        down the tree. */}
                    <span className={classes.treeBar}>
                        <span className={classes.treeBarFill} style={{ width: `${pct}%`, background: tone.bar }} />
                    </span>
                    <span className={classes.countValuePill} style={{ background: tone.bg, color: tone.fg }}>
                        {count}
                    </span>
                </span>
            </div>
            {open && <div className={classes.treeChildren}>{children}</div>}
        </div>
    );
};

const initials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

// Brand orange - sampled from the actual Decorpot logo, the same palette the
// Move to Marketing AI review dashboard and the other redesigned pages use.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
// Deeper than ORANGE_DARK, for small text on the pale ORANGE_LIGHT band:
// #c1531f there is only 4.20:1, which is under the bar for 10-12px type.
const ORANGE_INK = "#b24a19";

// Level markers for the hierarchy rows. Brand tint for the top level, then
// neutral greys - deliberately NOT the department hues, since a badge tinted
// like Sales sitting on a Design branch would be actively misleading.
const LEVEL_BADGES = {
    department: { label: "Dept", bg: ORANGE_LIGHT, fg: ORANGE_INK },
    centre: { label: "EC", bg: "#eef2f8", fg: "#4a5a70" },
    manager: { label: "Mgr", bg: "#f1f2f5", fg: "#5c6779" },
};

// A deeper, duller green than Operations' #1baf7a on purpose. The department
// hues are a categorical set with fixed meaning on this page, so an action
// button sharing one of them would read as a category. White on this is 4.9:1.
const EXPORT_GREEN = "#2e7d32";

// Indeterminate bar shown while a day is loading. Matches the banner gradient
// so the two read as the same system.
const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(244,126,68,0.16)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
    // The page is a fixed-height flex column, not a tall stack: the banner,
    // filters, tiles, tabs and pagination hold their natural height and the
    // table takes whatever is left. Nothing below the fold, and the pagination
    // stays on screen instead of being buried under the rows.
    //
    // NOT 100vh: this renders inside the app shell's antd <Content>, which adds
    // 16px margin + 24px padding beneath a 64px sticky <Header>. That 144px of
    // chrome has to come off, or the shell grows a second scrollbar.
    page: {
        padding: 16,
        background: "#f6f7f9",
        height: "calc(100vh - 144px)",
        // Floor for very short windows - below this the shell scrolls, which
        // is a better failure than squeezing the table to nothing.
        minHeight: 420,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // The shell drops to 12px padding on small screens.
        [theme.breakpoints.down("sm")]: { height: "calc(100vh - 120px)" },
    },
    // Everything above the table holds its own height; only the table flexes.
    fixedBlock: { flexShrink: 0 },
    // One per tab: each panel is itself a column so its toolbar and pagination
    // stay put while only its rows scroll.
    tabPanel: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    scrollArea: { flex: 1, minHeight: 0, overflow: "auto" },
    banner: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 16,
        background: HEADER_GRADIENT,
        borderRadius: 14,
        padding: "13px 20px",
        color: "#fff",
        marginBottom: 10,
        boxShadow: "0 6px 20px rgba(31,58,95,0.20)",
        // Soft bloom so the banner reads as lit rather than a flat gradient.
        "&::after": {
            content: '""',
            position: "absolute",
            top: -90,
            right: -40,
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },
    bannerIconWrap: {
        width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(255,255,255,0.16)", border: "2px solid rgba(255,255,255,0.28)",
    },
    bannerTitle: { fontSize: 21, fontWeight: 700, margin: 0, textAlign: "left" },
    bannerSub: { margin: "3px 0 0", fontSize: 12.5, opacity: 0.85, textAlign: "left" },
    // Darker than a translucent-white chip: this sits toward the bright end of
    // the gradient, where white-on-blue washes out.
    bannerDateChip: {
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 13px", borderRadius: 20,
        background: "rgba(0,0,0,0.26)", color: "#fff",
        fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0,
    },
    bannerRight: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginLeft: "auto" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Solid white with a dark ring rather than a translucent icon button - the
    // ring is what defines the EDGE, since white on the bright end of the
    // gradient is only ~2:1, and the word "Refresh" is what makes it findable.
    refreshButton: {
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 16px", borderRadius: 999, border: "none",
        background: "#fff", color: ORANGE_DARK,
        fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2, fontFamily: "inherit",
        cursor: "pointer",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.14), 0 3px 10px rgba(0,0,0,0.24)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: ORANGE_LIGHT, transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },
    lastUpdatedText: {
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 600, color: "#fff",
        background: "rgba(0,0,0,0.42)", padding: "3px 10px", borderRadius: 999,
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        background: "#FFD2B5", animation: "$pulseDot 2s ease-in-out infinite",
    },
    // Reserved slot rather than a conditional element, so nothing shifts by 4px
    // each time a fetch starts.
    progressSlot: { height: 4, borderRadius: 999, overflow: "hidden", marginBottom: 10 },
    loadingBanner: {
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 12, padding: "9px 13px", borderRadius: 10,
        background: ORANGE_LIGHT, border: "1px solid #f6d3bd",
    },
    // Lead number rendered as the link itself - a bare "Open" said nothing
    // about which lead it would open.
    leadLink: {
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 9px", borderRadius: 999,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
        color: ORANGE, fontWeight: 700, fontSize: 12.5,
        textDecoration: "none", whiteSpace: "nowrap",
        transition: "background .15s ease, border-color .15s ease",
        "&:hover": { background: "#ffe3d2", borderColor: ORANGE, textDecoration: "none" },
    },
    filterBar: {
        background: SURFACE, borderRadius: 12, padding: "8px 14px", marginBottom: 10,
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    },
    // One control instead of three: the arrows are tucked inside the same pill
    // as the date input, so stepping a day and picking a date are visibly the
    // same thing rather than a nav widget sitting next to an unrelated field.
    dateControl: {
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: "2px 5px",
        borderRadius: 999,
        border: `1px solid ${GRID}`,
        background: "#fff",
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": { borderColor: ORANGE, boxShadow: `0 0 0 3px ${ORANGE}14` },
        "&:focus-within": { borderColor: ORANGE, boxShadow: `0 0 0 3px ${ORANGE}22` },
    },
    dateArrow: {
        color: INK_SOFT,
        padding: 5,
        "&:hover": { background: "#FFF7F2", color: ORANGE },
    },
    dateInputRoot: {
        fontSize: 13.5,
        fontWeight: 700,
        color: INK,
        "& input": {
            // The whole field is the picker's click target, so it has to look
            // clickable - a bare text cursor reads as "type here".
            cursor: "pointer",
            padding: "5px 2px",
            textAlign: "center",
            minWidth: 128,
            fontVariantNumeric: "tabular-nums",
        },
        // The whole pill opens the picker now, and we render our own calendar
        // icon on the left, so the browser's glyph would be a second one.
        "& input::-webkit-calendar-picker-indicator": { display: "none" },
        cursor: "pointer",
    },
    tile: {
        background: SURFACE, borderRadius: 12, padding: "10px 14px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)", height: "100%", borderTop: "3px solid transparent",
    },
    tileLabel: { fontSize: 11, color: INK_MUTED, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 },
    tileValue: { fontSize: 23, fontWeight: 700, color: INK, lineHeight: 1.2, marginTop: 2 },
    tileFoot: { fontSize: 11, color: INK_SOFT, marginTop: 2 },
    tileIcon: { float: "right", opacity: 0.16, fontSize: 30 },
    tableCard: {
        background: SURFACE,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        marginTop: 12,
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
    },
    cell: { fontSize: 13, color: INK, borderBottom: "1px solid #f2f2f0" },
    clickRow: { cursor: "pointer", "&:hover": { background: "#f7f9fc" } },
    deptChip: { height: 22, fontSize: 11.5, fontWeight: 600, color: "#fff" },
    stageChip: { height: 22, fontSize: 11.5, fontWeight: 600, maxWidth: 230 },
    expandRow: { background: "#fbfbfa" },
    emptyBox: { padding: "48px 16px", textAlign: "center", color: INK_MUTED },
    emptyIconWrap: {
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: ORANGE_LIGHT,
        border: `1px solid ${ORANGE}22`,
    },
    // ── Richer table treatment ───────────────────────────────────────────
    // `!important` because MUI's stickyHeader rule paints a plain background on
    // th, which would otherwise sit on top of the gradient.
    headCellRich: {
        // Solid, not a gradient: across a wide table the gradient's light end
        // left the right-hand headers washed out, and a flat band keeps every
        // label at the same 4.64:1. `!important` is still needed because MUI's
        // stickyHeader rule paints its own background on th.
        background: `${ORANGE_DARK} !important`,
        color: "#fff",
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        borderBottom: "none",
        whiteSpace: "nowrap",
    },
    // The nested breakdown inside an expanded manager. A LIGHTER blue than
    // headCellRich rather than the same gradient: it has to read as coloured,
    // but also as subordinate to the header of the table containing it, or the
    // two bands compete and the nesting stops being obvious.
    // The nested breakdown inside an expanded manager. A PALE band with dark
    // text rather than another solid dark one: it stays clearly coloured while
    // reading as subordinate to the header of the table containing it, which
    // two dark bands stacked would not.
    subHeadCell: {
        background: ORANGE_LIGHT,
        color: ORANGE_INK,
        fontWeight: 700,
        fontSize: 10.5,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        borderBottom: "none",
        padding: "7px 10px",
    },
    // Used by every tab whose rows open a drill-down. Kept as one class so the
    // promise looks identical wherever it is made - it used to be a grey span
    // on By Employee and a blue strip on By Manager, which read as two
    // different kinds of message.
    tableHint: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 12px",
        fontSize: 11.5,
        fontWeight: 600,
        color: ORANGE_INK,
        background: ORANGE_LIGHT,
        borderTop: `1px solid ${GRID}`,
    },
    zebraRow: {
        transition: "background .15s ease, box-shadow .15s ease",
        "&:nth-of-type(even)": { background: "#FBFCFE" },
        "&:hover": { background: "#FFF7F2", boxShadow: `inset 3px 0 0 ${ORANGE}` },
    },
    rankCell: {
        fontSize: 12, fontWeight: 800, color: INK_MUTED,
        fontVariantNumeric: "tabular-nums", width: 46,
    },
    // Number plus a proportional bar. The bar answers "how bad is this one
    // compared with the worst" at a glance, which a bare count never did.
    countCellWrap: { display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 96 },
    countValuePill: {
        display: "inline-flex", alignItems: "center", gap: 5,
        minWidth: 38, padding: "2px 10px", borderRadius: 999,
        fontWeight: 800, fontSize: 13, fontVariantNumeric: "tabular-nums",
    },
    miniBar: { width: 92, height: 5, borderRadius: 999, background: "#edf0f4", overflow: "hidden" },
    miniBarFill: { height: "100%", borderRadius: 999, transition: "width .45s ease" },
    tableToolbar: {
        display: "flex", gap: 10, alignItems: "center",
        padding: "12px 16px", flexWrap: "wrap",
        borderBottom: `1px solid ${GRID}`, background: "#FBFCFE",
    },
    tabs: { borderBottom: `1px solid ${GRID}`, minHeight: 42, flexShrink: 0 },
    tab: { minHeight: 42, fontSize: 13, fontWeight: 600, textTransform: "none" },
    avatar: { width: 30, height: 30, fontSize: 12, fontWeight: 700, marginRight: 10 },
    userCellInner: { display: "flex", alignItems: "center" },
    // ── Hierarchy tree ───────────────────────────────────────────────────
    treeWrap: { padding: "10px 4px 16px", flex: 1, minHeight: 0, overflow: "auto" },
    treeRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 12px",
        borderRadius: 10,
        cursor: "pointer",
        transition: "background .15s ease",
        "&:hover": { background: "#F4F7FC" },
    },
    // Each level is indented AND carries a rail, so at four levels deep it is
    // still obvious which parent a row belongs to.
    treeChildren: {
        marginLeft: 20,
        paddingLeft: 14,
        borderLeft: `1px solid ${GRID}`,
    },
    treeCaret: { width: 18, height: 18, flexShrink: 0, color: INK_MUTED },
    treeLabel: { fontWeight: 700, color: INK, fontSize: 13.5 },
    treeSub: { fontSize: 11.5, color: INK_MUTED },
    treeLeafLabel: { fontWeight: 600, color: "#2b3648", fontSize: 13 },
    treeCount: {
        marginLeft: "auto",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
    },
    treeBar: { width: 70, height: 5, borderRadius: 999, background: "#edf0f4", overflow: "hidden" },
    treeBarFill: { height: "100%", borderRadius: 999, transition: "width .45s ease" },
    levelBadge: {
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        padding: "2px 7px",
        borderRadius: 5,
        flexShrink: 0,
    },
    // Sits beside the date control. Yesterday is the default because that is
    // the day the overnight mail reports on - but nothing on screen said so,
    // so an empty "today" looked like a broken report.
    defaultDayHint: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        fontWeight: 600,
        color: "#5c6779",
        background: ORANGE_LIGHT,
        border: "1px solid #f6d3bd",
        borderRadius: 999,
        padding: "4px 11px",
        whiteSpace: "nowrap",
    },
    filterGroupLabel: {
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: INK_MUTED,
        whiteSpace: "nowrap",
    },
    // Reads as "one of these is on" rather than "three decorative badges".
    activeFilterNote: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 16px",
        fontSize: 12.5,
        fontWeight: 600,
        color: "#8a4b00",
        background: "#FFF4E5",
        borderTop: "1px solid #f5d9b0",
        borderBottom: "1px solid #f5d9b0",
    },
    // stickyHeader needs a bounded scrolling container - without one it has
    // nothing to stick inside and the header just scrolls away with the rows.
    // Bounded by the flex column above rather than a fixed vh, so the table
    // grows on a tall screen and shrinks on a laptop instead of pushing the
    // pagination off the bottom. stickyHeader still gets the bounded scrolling
    // container it needs.
    employeeTableWrap: { flex: 1, minHeight: 0, overflow: "auto" },
    scopeNote: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "0 0 12px",
        padding: "9px 13px",
        borderRadius: 10,
        background: ORANGE_LIGHT,
        border: "1px solid #f6d3bd",
        fontSize: 12,
        color: ORANGE_INK,
        fontWeight: 600,
    },
    dialogHead: { display: "flex", alignItems: "center", gap: 12, paddingRight: 40 },
    dialogClose: { position: "absolute", right: 10, top: 10 },

    // ── Drill-down dialog (same treatment as the call report popup) ──────
    dialogPaper: { borderRadius: 14, overflow: "hidden" },
    dialogTitleBar: {
        background: HEADER_GRADIENT,
        color: "#fff",
        padding: "14px 18px",
        position: "relative",
    },
    dialogCloseLight: {
        position: "absolute", right: 10, top: 10, color: "#fff",
        background: "rgba(0,0,0,0.22)",
        "&:hover": { background: "rgba(0,0,0,0.34)" },
    },
    dialogMetaLine: { fontSize: 12.5, color: "rgba(255,255,255,0.82)", marginTop: 2 },
    // A strip of the three facts a manager actually wants before reading rows:
    // how many, how many are still actionable, and when they came in.
    statStrip: {
        display: "flex", flexWrap: "wrap", gap: 10,
        padding: "12px 18px", background: "#F7F9FC",
        borderBottom: `1px solid ${GRID}`,
    },
    statBox: {
        flex: "1 1 150px", minWidth: 130,
        background: SURFACE, borderRadius: 10, padding: "9px 12px",
        border: `1px solid ${GRID}`,
    },
    statLabel: {
        fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
        textTransform: "uppercase", color: INK_MUTED,
    },
    statValue: { fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.3, marginTop: 1 },
    statFoot: { fontSize: 11, color: INK_SOFT },
    // Bounded so long lists scroll inside the dialog instead of pushing the
    // close button off the bottom of the screen.
    dialogTableWrap: { maxHeight: "48vh", overflow: "auto" },
    dialogHeadCell: {
        fontWeight: 700, fontSize: 11.5, letterSpacing: 0.04,
        textTransform: "uppercase", color: "#fff", whiteSpace: "nowrap",
        // `!important` because MUI's stickyHeader rule paints a plain
        // background on th and would otherwise sit over the gradient.
        // Solid, matching the main table - see headCellRich.
        background: `${ORANGE_DARK} !important`,
        borderBottom: "none",
    },
    timePill: {
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 9px", borderRadius: 999,
        background: "#f1f4f8", color: INK_SOFT,
        fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
}));

// Yesterday by default - the same day the daily mail reports on, so a manager
// opening this after reading the mail lands on exactly those rows.
const yesterday = () => moment().subtract(1, "day").format("YYYY-MM-DD");

const MissedCallReport = (props) => {
    const classes = useStyles();

    const [date, setDate] = useState(yesterday());
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    // Set only on a 403 from the report endpoint. Kept separate from the
    // ordinary error path: "you may not see this" and "this failed to load"
    // need different screens, and a snackbar over an empty table reads as the
    // latter when it is really the former.
    const [forbidden, setForbidden] = useState(false);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const [tab, setTab] = useState(0);
    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [drillUser, setDrillUser] = useState(null);

    const fetchReport = useCallback(async (day) => {
        setLoading(true);
        try {
            const { data } = await HttpService.getMissedCallReport({ date: day });
            if (data?.success) {
                setForbidden(false);
                setReport(data);
                setLastUpdated(new Date());
            } else {
                props.setSnackInfo(data?.message || "Could not load the missed call report", "error");
            }
        } catch (error) {
            if (error?.response?.status === 403) {
                // No snackbar here - the full-page notice below says it
                // better, and stays on screen instead of timing out.
                setForbidden(true);
                setReport(null);
                setLastUpdated(null);
            } else {
                props.setSnackInfo(error?.response?.data?.message || "Could not load the missed call report", "error");
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchReport(date);
        setPage(0);
        setDrillUser(null);
    }, [date, fetchReport]);

    const dateInputRef = useRef(null);

    // Clicking the TEXT of a native date input does not open the calendar -
    // only the browser's small indicator glyph does, which is a ~14px target
    // most people never find. showPicker() is what makes the whole pill a
    // trigger. It must run inside a user gesture, and it throws if the element
    // is not visible or already open, so the call is guarded and focus is the
    // fallback. Supported in Chrome/Edge 99+, Firefox 101+, Safari 16+.
    const openDatePicker = () => {
        const el = dateInputRef.current;
        if (!el || loading) return;
        try {
            if (typeof el.showPicker === "function") el.showPicker();
        } catch (e) {
            /* not user-activated, or already open - focusing below still helps */
        }
        el.focus();
    };

    const shiftDay = (delta) => {
        const next = moment(date).add(delta, "day");
        if (next.isAfter(moment(), "day")) return;
        setDate(next.format("YYYY-MM-DD"));
    };

    const rows = report?.rows || [];
    const byDepartment = report?.byDepartment || [];
    const byEmployee = report?.byEmployee || [];
    const byManager = report?.byManager || [];
    const unattributed = report?.unattributed || [];
    const hierarchy = report?.hierarchy || [];
    // "manager" / "experienceCenter" / "all" - the server decides, based on the
    // signed-in user's roles. Shown so a manager understands why the numbers
    // here are smaller than the company-wide figures they may have seen.
    const scopeLevel = report?.scope?.level || "all";
    const totals = report?.totals || {};

    const isToday = moment(date).isSame(moment(), "day");
    const isYesterday = date === yesterday();
    const atToday = moment(date).isSameOrAfter(moment(), "day");

    const employeeRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return byEmployee.filter((e) => {
            if (deptFilter && e.department !== deptFilter) return false;
            if (!term) return true;
            return [e.employeeName, e.team, e.managerL1?.name, e.managerL2?.name]
                .filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
        });
    }, [byEmployee, search, deptFilter]);

    const pagedEmployees = useMemo(
        () => employeeRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [employeeRows, page, rowsPerPage]
    );

    // The rows behind the employee the manager just clicked.
    const drillRows = useMemo(
        () => (drillUser ? rows.filter((r) => String(r.employeeId) === String(drillUser.employeeId)) : []),
        [rows, drillUser]
    );

    const exportExcel = () => {
        if (!rows.length) {
            props.setSnackInfo("Nothing to export for this date", "warning");
            return;
        }
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byEmployee.map((e) => ({
            "Employee Name": e.employeeName,
            "Department": e.department,
            "Team": e.team,
            "Manager (L1)": e.managerL1?.name || "",
            "Manager (L2)": e.managerL2?.name || "",
            "Total Missed Calls": e.totalMissedCalls,
        }))), "By Employee");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map((r) => ({
            "Employee Name": r.employeeName,
            "Department": r.department,
            "Team": r.team,
            "Manager (L1)": r.managerL1?.name || "",
            "Manager (L2)": r.managerL2?.name || "",
            "Customer Name": r.customerName || "",
            "Current Stage": r.leadStage || "",
            "Lead No": r.leadNo || "",
            "Project No": r.leadProjectNo || "",
            "Call Date": r.callDate,
            "Call Time": r.callTime,
            "Missed Call Status": r.missedCallStatus,
            "Lead Link": r.leadLink || "",
        }))), "All Missed Calls");
        XLSX.writeFile(wb, `Missed_Call_Report_${date}.xlsx`);
    };

    const tiles = [
        { label: "Missed Calls", value: totals.reportedCalls ?? 0, foot: `${totals.missedCalls ?? 0} recorded in Runo`, icon: <PhoneMissedIcon className={classes.tileIcon} />, color: "#2a78d6" },
        { label: "Employees Affected", value: totals.employees ?? 0, foot: "with at least one missed call", icon: <PeopleAltOutlinedIcon className={classes.tileIcon} />, color: "#eb6834" },
        { label: "Managers Notified", value: totals.managers ?? 0, foot: "L1 managers mailed for this day", icon: <SupervisorAccountOutlinedIcon className={classes.tileIcon} />, color: "#1baf7a" },
        { label: "Unattributed", value: totals.unattributedCalls ?? 0, foot: "no employee mapped", icon: <HelpOutlineIcon className={classes.tileIcon} />, color: "#8a8984" },
    ];

    const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    // Bars are scaled to the worst count in each table, not to a fixed ceiling,
    // so the shading still ranks people on a quiet day.
    // Top-level bars scale to the biggest department so the departments are
    // comparable with each other; every deeper level scales to its own parent.
    const hierarchyPeak = useMemo(
        () => hierarchy.reduce((m, d) => Math.max(m, d.totalMissedCalls || 0), 0),
        [hierarchy]
    );
    const peakEmployeeCount = useMemo(
        () => byEmployee.reduce((m, e) => Math.max(m, e.totalMissedCalls || 0), 0),
        [byEmployee]
    );
    const peakManagerCount = useMemo(
        () => byManager.reduce((m, x) => Math.max(m, x.totalMissedCalls || 0), 0),
        [byManager]
    );

    // Count + proportional bar. Shared so the employee table, the manager table
    // and the nested breakdown all read on the same scale.
    const renderCount = (value, max) => {
        const tone = countTone(value, max);
        // The 4% floor keeps a single missed call visible as a sliver, but a
        // count of zero must draw nothing at all - a stub of bar under "0"
        // reads as "a little", which is the opposite of the truth.
        const pct = value > 0 && max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
        return (
            <div className={classes.countCellWrap}>
                <span className={classes.countValuePill} style={{ background: tone.bg, color: tone.fg }}>
                    {value}
                </span>
                <span className={classes.miniBar}>
                    <span className={classes.miniBarFill} style={{ width: `${pct}%`, background: tone.bar }} />
                </span>
            </div>
        );
    };

    // The lead NUMBER is the link. "Open ↗" was the same four characters on
    // every row, so nothing on screen said which lead you were about to open,
    // and the numbers could not be read, compared or copied out of the report.
    // Falls back to the project number when a lead carries no lead_no.
    const renderLeadLink = (row) => {
        const label = row?.leadNo ? `#${row.leadNo}` : row?.leadProjectNo || null;
        if (!row?.leadLink) {
            return <span style={{ color: INK_MUTED }}>{label || "No lead linked"}</span>;
        }
        return (
            <MuiTooltip title={`Open lead ${label || ""} in a new tab`}>
                <a
                    href={row.leadLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.leadLink}
                    onClick={(e) => e.stopPropagation()}
                >
                    <LinkOutlinedIcon style={{ fontSize: 13 }} />
                    {label || "Lead"}
                </a>
            </MuiTooltip>
        );
    };

    // Facts a manager wants before reading the rows: how many calls, how many
    // still point at a lead they can act on, and the window they arrived in.
    const drillStats = useMemo(() => {
        const linked = drillRows.filter((r) => r.leadLink).length;
        const times = drillRows
            .map((r) => r.callAt && moment(r.callAt))
            .filter((m) => m && m.isValid())
            .sort((a, b) => a - b);
        return {
            total: drillRows.length,
            linked,
            unlinked: drillRows.length - linked,
            first: times[0] ? times[0].format("hh:mm A") : "-",
            last: times[times.length - 1] ? times[times.length - 1].format("hh:mm A") : "-",
        };
    }, [drillRows]);

    // Returned BEFORE the dashboard, not layered over it. The report lists
    // named employees and the customers they missed, so an unauthorised viewer
    // should not see the banner, the filters or the shape of the page - only
    // that this is not theirs to open.
    if (forbidden) {
        return (
            <div className={classes.page}>
                <div className={`${classes.banner} ${classes.fixedBlock}`}>
                    <div className={classes.bannerIconWrap}>
                        <LockOutlinedIcon style={{ fontSize: 26 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 className={classes.bannerTitle}>Missed Call Report</h2>
                        <p className={classes.bannerSub}>Restricted report</p>
                    </div>
                </div>

                <div className={classes.emptyBox} style={{ background: "#fff", borderRadius: 14, padding: "56px 24px" }}>
                    <div
                        className={classes.emptyIconWrap}
                        style={{ width: 66, height: 66, margin: "0 auto 14px", background: "#fdecea", borderColor: "#f5c6c0" }}
                    >
                        <LockOutlinedIcon style={{ fontSize: 30, color: "#b03a2e" }} />
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#334155" }}>
                        You do not have access to this report
                    </div>
                    <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6, maxWidth: 520, margin: "6px auto 0" }}>
                        It shows named employees and the customers whose calls they missed, so it is limited to
                        Sales and Design managers — SM, ODM, AGM and DGM — and to Admin, Center Head and
                        Business Head.
                    </div>
                    <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 14 }}>
                        If you believe you should have access, ask your reporting manager to raise it with the ERP team.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.page}>
            <div className={`${classes.banner} ${classes.fixedBlock}`}>
                <div className={classes.bannerIconWrap}>
                    <CallMissedOutgoingIcon style={{ fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 className={classes.bannerTitle}>Missed Call Report</h2>
                    <p className={classes.bannerSub}>
                        Inbound customer calls that were not answered, by employee and reporting manager
                    </p>
                </div>
                {/* Says which day the figures cover, right beside them, rather
                    than leaving it to be read off the filter bar below. */}
                <div className={classes.bannerDateChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                    {moment(date).format("ddd, DD MMM YYYY")}
                    {isToday && <span style={{ opacity: 0.75, fontWeight: 600 }}>· today</span>}
                </div>
                <div className={classes.bannerRight}>
                    <MuiTooltip
                        title={
                            lastUpdated
                                ? `Last pulled at ${moment(lastUpdated).format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest calls`
                                : "Fetch the latest calls"
                        }
                    >
                        {/* A labelled button rather than an IconButton: the word
                            "Refresh" is what makes the control findable, and a
                            tooltip alone only helps people who already hovered.
                            The span is required because a disabled button fires
                            no mouse events, so the tooltip would die mid-refresh. */}
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className={classes.refreshButton}
                                onClick={() => fetchReport(date)}
                                disabled={loading}
                            >
                                <RefreshIcon
                                    className={loading ? classes.refreshSpinning : undefined}
                                    style={{ fontSize: 17 }}
                                />
                                {loading ? "Refreshing…" : "Refresh"}
                            </button>
                        </span>
                    </MuiTooltip>
                    {lastUpdated && (
                        <div className={classes.lastUpdatedText}>
                            <span className={classes.liveDot} />
                            {/* Relative beats a clock time: it answers "is this
                                stale?" without the reader doing the arithmetic. */}
                            {loading ? "Fetching latest…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                        </div>
                    )}
                </div>
            </div>

            {/* Reserved slot, so starting a fetch does not jog the page by 4px. */}
            <div className={`${classes.progressSlot} ${classes.fixedBlock}`}>{loading ? <BrandLinearProgress /> : null}</div>

            {/* The bar above shows that something is happening; this says what. */}
            {loading && (
                <div className={`${classes.loadingBanner} ${classes.fixedBlock}`}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE }} />
                    <div style={{ minWidth: 0, textAlign: "left" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: ORANGE_DARK }}>
                            Loading missed calls…
                        </div>
                        <div style={{ fontSize: 11.5, color: "#5c6779" }}>
                            {`Pulling every unanswered call for ${moment(date).format("DD MMM YYYY")} and matching it to an employee.`}
                        </div>
                    </div>
                </div>
            )}

            {/* Controls are individually disabled while a day loads - clicking
                through to another date mid-fetch just queues a second request
                whose result races the first. */}
            <div className={`${classes.filterBar} ${classes.fixedBlock}`}>
                {/* Arrows and picker are one control. The long-form date with
                    its weekday still shows in the banner chip above, so nothing
                    is lost by the input rendering in the browser's own format. */}
                <div className={classes.dateControl}>
                    <MuiTooltip title="Previous day">
                        <span>
                            <IconButton
                                size="small"
                                className={classes.dateArrow}
                                disabled={loading}
                                onClick={() => shiftDay(-1)}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                    <MuiTooltip title="Click to pick a date">
                        {/* onClick sits on the TextField root, so a click on the
                            calendar icon, the date text or the padding around it
                            all bubble to the same handler. */}
                        <TextField
                            type="date"
                            variant="standard"
                            disabled={loading}
                            value={date}
                            inputRef={dateInputRef}
                            inputProps={{ max: moment().format("YYYY-MM-DD") }}
                            onChange={(e) => e.target.value && setDate(e.target.value)}
                            onClick={openDatePicker}
                            InputProps={{
                                disableUnderline: true,
                                className: classes.dateInputRoot,
                                startAdornment: (
                                    <InputAdornment position="start" style={{ marginRight: 4 }}>
                                        <DateRangeOutlinedIcon
                                            style={{ fontSize: 17, color: INK_SOFT, cursor: "pointer" }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </MuiTooltip>
                    <MuiTooltip title={atToday ? "Today has not finished yet" : "Next day"}>
                        <span>
                            <IconButton
                                size="small"
                                className={classes.dateArrow}
                                disabled={atToday || loading}
                                onClick={() => shiftDay(1)}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                </div>
                {/* Says what the date box is already showing you and why, so
                    nobody has to work out that "yesterday" was a choice. */}
                <MuiTooltip title="The overnight email reports on yesterday, so the dashboard opens on the same day. Use the arrows or the date box to look at any other day.">
                    <span className={classes.defaultDayHint}>
                        <InfoOutlinedIcon style={{ fontSize: 14 }} />
                        {isYesterday
                            ? "Showing yesterday by default — change the date to see another day"
                            : `Showing ${moment(date).format("DD MMM")} — the default is yesterday`}
                    </span>
                </MuiTooltip>
                <div style={{ flex: 1 }} />
                {isToday && (
                    <span style={{ fontSize: 12, color: "#9a6212", background: "#fff4e0", padding: "4px 10px", borderRadius: 6 }}>
                        Today is still in progress — the daily mail reports on yesterday
                    </span>
                )}
                {/* Refresh lives in the banner now - two of them a few pixels
                    apart just made the page look unfinished. */}
                <MuiTooltip
                    title={
                        rows.length
                            ? "Downloads two sheets: one row per employee, and every missed call"
                            : "Nothing to export for this date"
                    }
                >
                    <span>
                        <Button
                            icon={<GetAppIcon style={{ fontSize: 16, verticalAlign: "-3px" }} />}
                            disabled={loading || !rows.length}
                            onClick={exportExcel}
                            // Green only while it is actually clickable - antd
                            // paints its own disabled state, and an inline style
                            // would override it into a live-looking button that
                            // does nothing. EXPORT_GREEN is deliberately not the
                            // Operations department colour, which is also green:
                            // a button tinted like a category chip reads as one.
                            style={
                                loading || !rows.length
                                    ? undefined
                                    : {
                                        background: EXPORT_GREEN,
                                        borderColor: EXPORT_GREEN,
                                        color: "#fff",
                                        fontWeight: 600,
                                    }
                            }
                        >
                            &nbsp;Download Excel
                        </Button>
                    </span>
                </MuiTooltip>
            </div>

            <Grid container spacing={1} className={classes.fixedBlock}>
                {tiles.map((t) => (
                    <Grid item xs={12} sm={6} md={3} key={t.label}>
                        <Paper elevation={0} className={classes.tile} style={{ borderTopColor: t.color }}>
                            {t.icon}
                            <div className={classes.tileLabel}>{t.label}</div>
                            <div className={classes.tileValue}>
                                {loading ? <Skeleton width={70} /> : (t.value || 0).toLocaleString("en-IN")}
                            </div>
                            <div className={classes.tileFoot}>{t.foot}</div>
                        </Paper>
                    </Grid>
                ))}
            </Grid>


            <Paper elevation={0} className={classes.tableCard}>
                <Tabs value={tab} onChange={(e, v) => { setTab(v); setPage(0); }} indicatorColor="primary" textColor="primary" className={classes.tabs}>
                    <Tab className={classes.tab} label={`Hierarchy (${hierarchy.length})`} />
                    <Tab className={classes.tab} label={`By Employee (${byEmployee.length})`} />
                    <Tab className={classes.tab} label={`By Manager (${byManager.length})`} />
                    <Tab className={classes.tab} label={`Unattributed (${unattributed.length})`} />
                </Tabs>

                {tab === 0 && (
                    <div className={classes.treeWrap}>
                        {/* A manager seeing only their own slice needs telling
                            why, or the totals here look like a bug against the
                            company-wide figures they saw elsewhere. */}
                        {scopeLevel !== "all" && (
                            <div className={classes.scopeNote} style={{ margin: "0 12px 12px" }}>
                                <InfoOutlinedIcon style={{ fontSize: 16 }} />
                                {scopeLevel === "manager"
                                    ? "Showing your own team only — the people reporting to you, plus anyone tagged to you."
                                    : "Showing your Experience Centres only — everyone in your department at those centres."}
                            </div>
                        )}

                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <div key={i} style={{ padding: "10px 14px" }}>
                                    <Skeleton height={26} />
                                </div>
                            ))
                        ) : !hierarchy.length ? (
                            <div className={classes.emptyBox}>
                                <div className={classes.emptyIconWrap} style={{ width: 62, height: 62, margin: "0 auto 12px" }}>
                                    <AccountTreeOutlinedIcon style={{ fontSize: 28, color: ORANGE }} />
                                </div>
                                <div style={{ fontWeight: 700, color: "#334155" }}>No missed calls to break down</div>
                                <div style={{ fontSize: 12.5, marginTop: 3 }}>
                                    Nothing was missed on this date, so there is no hierarchy to show.
                                </div>
                            </div>
                        ) : (
                            hierarchy.map((dept) => (
                                <TreeNode
                                    key={dept.key}
                                    nodeKey={dept.key}
                                    level="department"
                                    label={dept.department}
                                    sub={`${dept.centres.length} centre${dept.centres.length === 1 ? "" : "s"} · ${dept.employees} employee${dept.employees === 1 ? "" : "s"}`}
                                    count={dept.totalMissedCalls}
                                    max={hierarchyPeak}
                                    classes={classes}
                                    expanded={expanded}
                                    onToggle={toggleExpand}
                                >
                                    {dept.centres.map((centre) => (
                                        <TreeNode
                                            key={centre.key}
                                            nodeKey={centre.key}
                                            level="centre"
                                            label={centre.experienceCenterName}
                                            sub={`${centre.managers.length} manager${centre.managers.length === 1 ? "" : "s"} · ${centre.employees} employee${centre.employees === 1 ? "" : "s"}`}
                                            count={centre.totalMissedCalls}
                                            max={dept.totalMissedCalls}
                                            classes={classes}
                                            expanded={expanded}
                                            onToggle={toggleExpand}
                                        >
                                            {centre.managers.map((mgr) => (
                                                <TreeNode
                                                    key={mgr.key}
                                                    nodeKey={mgr.key}
                                                    level="manager"
                                                    label={mgr.managerL1?.name || "— no manager mapped —"}
                                                    sub={mgr.managerL2?.name ? `L2: ${mgr.managerL2.name}` : ""}
                                                    count={mgr.totalMissedCalls}
                                                    max={centre.totalMissedCalls}
                                                    classes={classes}
                                                    expanded={expanded}
                                                    onToggle={toggleExpand}
                                                >
                                                    {mgr.employees.map((emp) => {
                                                        const tone = countTone(emp.totalMissedCalls, mgr.totalMissedCalls);
                                                        return (
                                                            <MuiTooltip key={emp.key} title="Open this person's missed calls">
                                                                <div
                                                                    className={classes.treeRow}
                                                                    onClick={() =>
                                                                        setDrillUser(
                                                                            byEmployee.find(
                                                                                (e) => String(e.employeeId) === String(emp.employeeId)
                                                                            ) || {
                                                                                employeeId: emp.employeeId,
                                                                                employeeName: emp.employeeName,
                                                                                team: emp.team,
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    {/* Leaf rows have no caret, but keep its
                                                                        width so their labels stay aligned
                                                                        with the branch labels above. */}
                                                                    <span className={classes.treeCaret} />
                                                                    <Avatar
                                                                        className={classes.avatar}
                                                                        style={{ width: 24, height: 24, fontSize: 10, background: tone.bar }}
                                                                    >
                                                                        {initials(emp.employeeName)}
                                                                    </Avatar>
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div className={classes.treeLeafLabel}>{emp.employeeName}</div>
                                                                        {emp.team ? <div className={classes.treeSub}>{emp.team}</div> : null}
                                                                    </div>
                                                                    <span className={classes.treeCount}>
                                                                        <span
                                                                            className={classes.countValuePill}
                                                                            style={{ background: tone.bg, color: tone.fg }}
                                                                        >
                                                                            {emp.totalMissedCalls}
                                                                        </span>
                                                                    </span>
                                                                </div>
                                                            </MuiTooltip>
                                                        );
                                                    })}
                                                </TreeNode>
                                            ))}
                                        </TreeNode>
                                    ))}
                                </TreeNode>
                            ))
                        )}
                    </div>
                )}

                {tab === 1 && (
                    <div className={classes.tabPanel}>
                        <div className={`${classes.tableToolbar} ${classes.fixedBlock}`}>
                            <TextField
                                size="small" variant="outlined" placeholder="Search employee, team or manager…"
                                disabled={loading}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                style={{ minWidth: 300 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <span className={classes.filterGroupLabel}>Filter by dept</span>
                            {DEPT_ORDER.filter((d) => byDepartment.some((x) => x.department === d)).map((d) => {
                                const active = deptFilter === d;
                                const count = byDepartment.find((x) => x.department === d)?.totalMissedCalls ?? 0;
                                return (
                                    <MuiTooltip
                                        key={d}
                                        title={active
                                            ? `Showing ${d} only — click again to go back to all departments`
                                            : `Show only ${d} (${count} missed call${count === 1 ? "" : "s"})`}
                                    >
                                        <Chip
                                            label={`${d} · ${count}`}
                                            size="small"
                                            onClick={() => { setDeptFilter(active ? null : d); setPage(0); }}
                                            // onDelete puts a ✕ on the active chip, which is
                                            // what actually tells people the filter can be
                                            // switched off. It runs the same toggle, so the
                                            // ✕ and a second click do the same thing.
                                            onDelete={active ? () => { setDeptFilter(null); setPage(0); } : undefined}
                                            className={classes.deptChip}
                                            style={{
                                                background: active ? colorFor(d) : "#eef0f3",
                                                color: active ? "#fff" : INK_SOFT,
                                                cursor: "pointer",
                                                fontWeight: active ? 700 : 600,
                                                boxShadow: active ? `0 0 0 2px ${colorFor(d)}33` : "none",
                                            }}
                                        />
                                    </MuiTooltip>
                                );
                            })}
                        </div>

                        <div className={`${classes.tableHint} ${classes.fixedBlock}`}>
                            <InfoOutlinedIcon style={{ fontSize: 14 }} />
                            Click any employee below to see the calls they missed
                        </div>

                        {/* Only while a filter is on. A permanent hint becomes
                            wallpaper; this appears exactly when it is true. */}
                        {deptFilter && (
                            <div className={`${classes.activeFilterNote} ${classes.fixedBlock}`}>
                                <FilterListIcon style={{ fontSize: 16 }} />
                                Showing <strong>{deptFilter}</strong> only — {employeeRows.length} of {byEmployee.length} employees.
                                {/* type="link" because this is an antd Button -
                                    the default has a border and a white fill,
                                    which looks like a stray control dropped
                                    into the amber strip. */}
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => { setDeptFilter(null); setPage(0); }}
                                    style={{ marginLeft: "auto", padding: 0, height: "auto", fontWeight: 700, color: "#8a4b00" }}
                                >
                                    Show all departments
                                </Button>
                            </div>
                        )}

                        <TableContainer className={classes.employeeTableWrap}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.headCellRich} style={{ width: 46 }}>#</TableCell>
                                        <TableCell className={classes.headCellRich}>Employee</TableCell>
                                        <TableCell className={classes.headCellRich}>Department / Team</TableCell>
                                        {/* Sits right after the department, where the
                                            eye lands after identifying who this is -
                                            it was stranded on the far right, past two
                                            manager columns nobody scans for. */}
                                        <TableCell className={classes.headCellRich}>Missed Calls</TableCell>
                                        <TableCell className={classes.headCellRich}>Manager (L1)</TableCell>
                                        <TableCell className={classes.headCellRich}>Manager (L2)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        [...Array(8)].map((_, i) => (
                                            <TableRow key={i}><TableCell colSpan={6}><Skeleton height={26} /></TableCell></TableRow>
                                        ))
                                    ) : pagedEmployees.length ? (
                                        pagedEmployees.map((e, i) => (
                                            <TableRow
                                                key={String(e.employeeId)}
                                                className={`${classes.clickRow} ${classes.zebraRow}`}
                                                onClick={() => setDrillUser(e)}
                                            >
                                                <TableCell className={`${classes.cell} ${classes.rankCell}`}>
                                                    {page * rowsPerPage + i + 1}
                                                </TableCell>
                                                <TableCell className={classes.cell}>
                                                    <div className={classes.userCellInner}>
                                                        <Avatar
                                                            className={classes.avatar}
                                                            style={{
                                                                background: colorFor(e.department),
                                                                boxShadow: `0 0 0 2px ${colorFor(e.department)}22`,
                                                            }}
                                                        >
                                                            {initials(e.employeeName)}
                                                        </Avatar>
                                                        <strong>{e.employeeName}</strong>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.cell}>
                                                    <Chip label={e.department} size="small" className={classes.deptChip}
                                                        style={{ background: colorFor(e.department), marginRight: 6 }} />
                                                    <span style={{ fontSize: 12, color: INK_SOFT }}>{e.team}</span>
                                                </TableCell>
                                                <TableCell className={classes.cell}>
                                                    {renderCount(e.totalMissedCalls, peakEmployeeCount)}
                                                </TableCell>
                                                <TableCell className={classes.cell}>{e.managerL1?.name || "-"}</TableCell>
                                                <TableCell className={classes.cell}>{e.managerL2?.name || "-"}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow><TableCell colSpan={6}><div className={classes.emptyBox}>No missed calls on this date</div></TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={employeeRows.length}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                            className={classes.fixedBlock}
                        />
                    </div>
                )}

                {tab === 2 && (
                    <div className={classes.tabPanel}>
                        {/* Rows here EXPAND rather than open a dialog, which is
                            the opposite of every other tab - so it has to be
                            said, and it names both ways of doing it because the
                            caret is small and the row is the bigger target. */}
                        <div className={`${classes.tableHint} ${classes.fixedBlock}`}>
                            <InfoOutlinedIcon style={{ fontSize: 14 }} />
                            Click a manager's row, or the arrow beside it, to open the employees reporting to them
                        </div>

                        {/* stickyHeader needs a bounded scroller; scrollArea is
                            what gives it one, the same way employeeTableWrap does. */}
                        <TableContainer className={classes.scrollArea}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCellRich} style={{ width: 42 }} />
                                    <TableCell className={classes.headCellRich}>Manager (L1)</TableCell>
                                    <TableCell className={classes.headCellRich}>Departments</TableCell>
                                    <TableCell className={classes.headCellRich}>Missed Calls</TableCell>
                                    <TableCell className={classes.headCellRich}>Manager (L2)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [...Array(6)].map((_, i) => (
                                        <TableRow key={i}><TableCell colSpan={5}><Skeleton height={26} /></TableCell></TableRow>
                                    ))
                                ) : byManager.length ? (
                                    byManager.slice().sort((a, b) => {
                                        // Unmapped employees belong to nobody, so they sit at
                                        // the bottom rather than competing with real managers
                                        // for the top of the list on volume alone.
                                        const aNone = a.managerKey === "__NO_MANAGER__";
                                        const bNone = b.managerKey === "__NO_MANAGER__";
                                        if (aNone !== bNone) return aNone ? 1 : -1;
                                        return b.totalMissedCalls - a.totalMissedCalls;
                                    }).map((m) => {
                                        const key = m.managerKey;
                                        const open = !!expanded[key];
                                        // Comes from the group itself - an employee can
                                        // report to several L1s, so filtering byEmployee
                                        // on a single managerL1 id would miss them.
                                        const employees = m.byEmployee || [];
                                        return (
                                            <React.Fragment key={key}>
                                                <TableRow
                                                    hover
                                                    className={classes.zebraRow}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => toggleExpand(key)}
                                                >
                                                    <TableCell className={classes.cell}>
                                                        <IconButton size="small">
                                                            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell className={classes.cell}>
                                                        <strong>{m.managerL1?.name || "— no manager mapped —"}</strong>
                                                        <div style={{ fontSize: 11.5, color: INK_MUTED }}>{m.managerL1?.email || ""}</div>
                                                    </TableCell>
                                                    <TableCell className={classes.cell}>
                                                        {(m.departments || []).map((d) => (
                                                            <Chip key={d} label={d} size="small" className={classes.deptChip}
                                                                style={{ background: colorFor(d), marginRight: 4 }} />
                                                        ))}
                                                    </TableCell>
                                                    <TableCell className={classes.cell}>
                                                        {renderCount(m.totalMissedCalls, peakManagerCount)}
                                                    </TableCell>
                                                    <TableCell className={classes.cell}>
                                                        {m.managerL2?.name || "-"}
                                                        <div style={{ fontSize: 11.5, color: INK_MUTED }}>{m.managerL2?.email || ""}</div>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow className={classes.expandRow}>
                                                    <TableCell colSpan={5} style={{ padding: 0, borderBottom: open ? `1px solid ${GRID}` : "none" }}>
                                                        <Collapse in={open} timeout="auto" unmountOnExit>
                                                            <div className={classes.tableHint}>
                                                                <InfoOutlinedIcon style={{ fontSize: 14 }} />
                                                                Click any employee below to see the calls they missed
                                                            </div>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell className={classes.subHeadCell} style={{ width: 46 }}>#</TableCell>
                                                                        <TableCell className={classes.subHeadCell}>Employee</TableCell>
                                                                        <TableCell className={classes.subHeadCell}>Department / Team</TableCell>
                                                                        <TableCell className={classes.subHeadCell}>Missed Calls</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {employees.map((e, ei) => (
                                                                        <MuiTooltip key={String(e.employeeId)} title={`See ${e.employeeName}'s missed calls`}>
                                                                            <TableRow className={`${classes.clickRow} ${classes.zebraRow}`} onClick={() => setDrillUser(e)}>
                                                                                {/* Numbered within THIS manager's list, not the
                                                                                    page - there is no pagination in here, and a
                                                                                    global index would jump between managers. */}
                                                                                <TableCell className={`${classes.cell} ${classes.rankCell}`}>{ei + 1}</TableCell>
                                                                                <TableCell className={classes.cell}>
                                                                                    <div className={classes.userCellInner}>
                                                                                        <Avatar
                                                                                            className={classes.avatar}
                                                                                            style={{
                                                                                                background: colorFor(e.department),
                                                                                                boxShadow: `0 0 0 2px ${colorFor(e.department)}22`,
                                                                                            }}
                                                                                        >
                                                                                            {initials(e.employeeName)}
                                                                                        </Avatar>
                                                                                        <strong>{e.employeeName}</strong>
                                                                                    </div>
                                                                                </TableCell>
                                                                                <TableCell className={classes.cell}>{e.department}{e.team ? ` · ${e.team}` : ""}</TableCell>
                                                                                <TableCell className={classes.cell}>
                                                                                    {renderCount(e.totalMissedCalls, peakEmployeeCount)}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </MuiTooltip>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <TableRow><TableCell colSpan={5}><div className={classes.emptyBox}>No missed calls on this date</div></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                        </TableContainer>
                    </div>
                )}

                {tab === 3 && (
                    <TableContainer className={classes.scrollArea}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCellRich}>Customer</TableCell>
                                    <TableCell className={classes.headCellRich}>Current Stage</TableCell>
                                    <TableCell className={classes.headCellRich}>Call Time</TableCell>
                                    <TableCell className={classes.headCellRich}>Why it is unattributed</TableCell>
                                    <TableCell className={classes.headCellRich} align="right">Lead No</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {unattributed.length ? unattributed.map((u) => {
                                    const tone = stageTone(u.leadStage);
                                    return (
                                        <TableRow key={u.callId} hover className={classes.zebraRow}>
                                            <TableCell className={classes.cell}><strong>{u.customerName || "-"}</strong></TableCell>
                                            <TableCell className={classes.cell}>
                                                {u.leadStage
                                                    ? <Chip label={u.leadStage} size="small" className={classes.stageChip} style={{ background: tone.bg, color: tone.fg }} />
                                                    : <span style={{ color: INK_MUTED }}>-</span>}
                                            </TableCell>
                                            <TableCell className={classes.cell}>
                                                <span className={classes.timePill}>
                                                    <AccessTimeIcon style={{ fontSize: 12 }} />
                                                    {u.callTime}
                                                </span>
                                            </TableCell>
                                            <TableCell className={classes.cell} style={{ color: INK_SOFT }}>{u.reason}</TableCell>
                                            <TableCell className={classes.cell} align="right">
                                                {renderLeadLink(u)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) : (
                                    <TableRow><TableCell colSpan={5}><div className={classes.emptyBox}>Every missed call on this date is mapped to an employee</div></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Drill-down: the calls behind one employee's count. */}
            <Dialog
                open={!!drillUser}
                onClose={() => setDrillUser(null)}
                maxWidth="md"
                fullWidth
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle disableTypography className={classes.dialogTitleBar}>
                    <div className={classes.dialogHead}>
                        <Avatar
                            className={classes.avatar}
                            style={{
                                background: colorFor(drillUser?.department),
                                width: 40,
                                height: 40,
                                fontSize: 14,
                                border: "2px solid rgba(255,255,255,0.4)",
                            }}
                        >
                            {initials(drillUser?.employeeName)}
                        </Avatar>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 17, fontWeight: 700 }}>{drillUser?.employeeName}</div>
                            <div className={classes.dialogMetaLine}>
                                {drillUser?.department}{drillUser?.team ? ` · ${drillUser.team}` : ""}
                                {" · "}L1: {drillUser?.managerL1?.name || "-"}
                                {" · "}L2: {drillUser?.managerL2?.name || "-"}
                            </div>
                        </div>
                    </div>
                    <IconButton size="small" className={classes.dialogCloseLight} onClick={() => setDrillUser(null)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* The three facts worth knowing before reading a single row.
                    "Still linked" matters most: a missed call with no lead
                    behind it cannot be actioned from here. */}
                <div className={classes.statStrip}>
                    <div className={classes.statBox}>
                        <div className={classes.statLabel}>Missed Calls</div>
                        <div className={classes.statValue} style={{ color: "#b03a2e" }}>{drillStats.total}</div>
                        <div className={classes.statFoot}>on {moment(date).format("DD MMM YYYY")}</div>
                    </div>
                    <div className={classes.statBox}>
                        <div className={classes.statLabel}>Linked to a Lead</div>
                        <div className={classes.statValue}>{drillStats.linked}</div>
                        <div className={classes.statFoot}>
                            {drillStats.unlinked ? `${drillStats.unlinked} with no lead` : "every call is actionable"}
                        </div>
                    </div>
                    <div className={classes.statBox}>
                        <div className={classes.statLabel}>Call Window</div>
                        <div className={classes.statValue} style={{ fontSize: 16 }}>
                            {drillStats.first} — {drillStats.last}
                        </div>
                        <div className={classes.statFoot}>first to last missed call</div>
                    </div>
                </div>

                <DialogContent style={{ padding: 0 }}>
                    <div className={classes.dialogTableWrap}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.dialogHeadCell} style={{ width: 44 }}>#</TableCell>
                                    <TableCell className={classes.dialogHeadCell}>Customer</TableCell>
                                    <TableCell className={classes.dialogHeadCell}>Current Stage</TableCell>
                                    <TableCell className={classes.dialogHeadCell}>Project No</TableCell>
                                    <TableCell className={classes.dialogHeadCell}>Call Time</TableCell>
                                    <TableCell className={classes.dialogHeadCell} align="right">Lead No</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {drillRows.length ? drillRows.map((r, i) => {
                                    const tone = stageTone(r.leadStage);
                                    return (
                                        <TableRow key={r.callId} hover className={classes.clickRow}>
                                            <TableCell className={classes.cell} style={{ color: INK_MUTED, fontWeight: 600 }}>
                                                {i + 1}
                                            </TableCell>
                                            <TableCell className={classes.cell}>
                                                <strong>{r.customerName || "-"}</strong>
                                            </TableCell>
                                            <TableCell className={classes.cell}>
                                                {r.leadStage
                                                    ? <Chip label={r.leadStage} size="small" className={classes.stageChip} style={{ background: tone.bg, color: tone.fg }} />
                                                    : <span style={{ color: INK_MUTED, fontSize: 12.5 }}>No lead linked</span>}
                                            </TableCell>
                                            <TableCell className={classes.cell}>{r.leadProjectNo || "-"}</TableCell>
                                            <TableCell className={classes.cell}>
                                                <span className={classes.timePill}>
                                                    <AccessTimeIcon style={{ fontSize: 12 }} />
                                                    {r.callTime}
                                                </span>
                                            </TableCell>
                                            <TableCell className={classes.cell} align="right">
                                                {renderLeadLink(r)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) : (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <div className={classes.emptyBox}>No calls behind this count</div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogContent>
                <DialogActions style={{ borderTop: `1px solid ${GRID}` }}>
                    <Button type="primary" onClick={() => setDrillUser(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default connect(null, { setSnackInfo })(MissedCallReport);
